'use server';

import type { ActionResult } from '@/shared/types/actions';
import { SessionService } from '@/contexts/authentication';
import { logger } from '@/shared/infrastructure/logging/logger';

export async function generateCover(input: {
  bookId: string;
}): Promise<ActionResult<{ jobId?: string }>> {
  try {
    const familyId = await SessionService.getFamilyId();
    const userId = await SessionService.getUserId();
    if (!familyId || !userId) return { success: false, error: 'Not authenticated' };

    const { IllustrationGenerationService } = await import('@/contexts/illustrations/services/BookGenerationService');
    const { getIllustrationRepository } = await import('@/contexts/illustrations/repositories');
    const { getCharacterRepository } = await import('@/contexts/characters/repositories');
    const { getTokenManager } = await import('@/contexts/subscriptions/services/getTokenManager');
    const { getImageGenerationQueue } = await import('@/contexts/ai/infrastructure/imageGenerationQueue');

    const illustrationRepo = getIllustrationRepository();
    const illustration = await illustrationRepo.getById(input.bookId, familyId);
    if (!illustration) return { success: false, error: 'BOOK_NOT_FOUND' };

    const service = new IllustrationGenerationService(
      illustrationRepo,
      getCharacterRepository(),
      getTokenManager(),
      getImageGenerationQueue(),
    );

    // Fetch ageGroup from story for age-appropriate image generation
    let ageGroup: string | undefined;
    try {
      const { getStoryRepository } = await import('@/contexts/stories/repositories');
      const story = await getStoryRepository().getById(illustration.storyId);
      ageGroup = story?.ageGroup ?? undefined;
    } catch { /* best-effort */ }

    const result = await service.startCoverGenerationAsync(illustration, familyId, ageGroup);

    if (!result.success) {
      return { success: false, error: result.error || 'COVER_GENERATION_FAILED' };
    }

    return { success: true, data: { jobId: result.jobId } };
  } catch (e: unknown) {
    // Server-side log preserves the full stack for ops; the client only
    // gets a stable, non-empty error code so toasts never render blank.
    logger.error('[generateCover] failed', { bookId: input.bookId, error: e });
    const raw = e instanceof Error ? e.message : '';
    const message = raw && raw.trim().length > 0 ? raw : 'COVER_GENERATION_FAILED';
    return { success: false, error: message };
  }
}
